import React from 'react';

/**
 * InboxItemCard Component
 * Renders an operational inbox card matching the exact styling and interactive states.
 */
export default function InboxItemCard({
    item,
    isSelected = false,
    onToggleSelect,
    onToggleBookmark,
    onToggleArchive,
    onToggleRead,
    onActionClick,
}) {
    // Dynamic tag styling
    const getTypeTagStyles = (type) => {
        switch (type) {
            case 'APPROVAL':
                return 'bg-amber-300/10 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-300/30 dark:border-amber-800/50';
            case 'ACTION REQUIRED':
                return 'bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/50';
            case 'MENTION':
                return 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/50';
            case 'ALERT':
                return 'bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/50';
            case 'MILESTONE':
                return 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/50';
            case 'TASK UPDATE':
                return 'bg-blue-100/50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800/50';
            case 'DOCUMENT':
            default:
                return 'bg-slate-100 dark:bg-[#252525] text-slate-600 dark:text-slate-300 border-slate-200 dark:border-[#383838]';
        }
    };

    const getPriorityTagStyles = (priorityLevel) => {
        switch (priorityLevel) {
            case 'critical':
                return 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400';
            case 'high':
                return 'bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400';
            case 'medium':
                return 'bg-slate-100 dark:bg-[#252525] text-slate-600 dark:text-slate-400';
            default:
                return 'bg-slate-100 dark:bg-[#252525] text-slate-600 dark:text-slate-400';
        }
    };

    const getActorAvatarStyles = (actor) => {
        const rawBg = actor?.bgColor || '';
        const initials = (actor?.initials || '').toUpperCase();

        // Amber / Design / Review (e.g. Hassan Raza HR)
        if (rawBg.includes('amber') || initials === 'HR') {
            return 'bg-amber-100 dark:bg-amber-950/70 text-amber-900 dark:text-amber-300 border-amber-300/80 dark:border-amber-700/60';
        }
        // Blue / Admin / CRE (e.g. Ali Haider AH, DymaxTech DT)
        if (rawBg.includes('blue') || rawBg.includes('#03488A') || initials === 'AH' || initials === 'DT') {
            return 'bg-blue-100 dark:bg-blue-950/70 text-[#03488A] dark:text-[#00AAF1] border-blue-300/80 dark:border-blue-700/60';
        }
        // Purple / Mentions / Dev (e.g. Trevor Babyak TB, Sarah Chen SC)
        if (rawBg.includes('purple') || initials === 'TB' || initials === 'SC') {
            return 'bg-purple-100 dark:bg-purple-950/70 text-purple-900 dark:text-purple-300 border-purple-300/80 dark:border-purple-700/60';
        }
        // Rose / Red / Critical (e.g. System Monitor SM, Customer Support CS)
        if (rawBg.includes('red') || rawBg.includes('rose') || initials === 'SM' || initials === 'CS') {
            return 'bg-rose-100 dark:bg-rose-950/70 text-rose-900 dark:text-rose-300 border-rose-300/80 dark:border-rose-700/60';
        }
        // Emerald / Green / Approvals (e.g. Michael Scott MS)
        if (rawBg.includes('emerald') || rawBg.includes('green') || initials === 'MS') {
            return 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-900 dark:text-emerald-300 border-emerald-300/80 dark:border-emerald-700/60';
        }

        // Deterministic palette based on initials
        const charCode = initials ? initials.charCodeAt(0) : 0;
        const variants = [
            'bg-blue-100 dark:bg-blue-950/70 text-[#03488A] dark:text-[#00AAF1] border-blue-300/80 dark:border-blue-700/60',
            'bg-purple-100 dark:bg-purple-950/70 text-purple-900 dark:text-purple-300 border-purple-300/80 dark:border-purple-700/60',
            'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-900 dark:text-emerald-300 border-emerald-300/80 dark:border-emerald-700/60',
            'bg-amber-100 dark:bg-amber-950/70 text-amber-900 dark:text-amber-300 border-amber-300/80 dark:border-amber-700/60',
        ];
        return variants[charCode % variants.length] || 'bg-slate-100 dark:bg-[#252525] text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-700';
    };

    const getActionButtonClass = (style) => {
        const base = 'h-8 px-3.5 rounded-xl font-bold text-xs shadow-xs transition-all inline-flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer select-none leading-none';
        switch (style) {
            case 'brand-gradient':
                return `text-white hover:opacity-90 active:scale-95 ${base}`;
            case 'primary-success':
                return `text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 ${base}`;
            case 'primary-blue':
                return `text-white bg-blue-600 hover:bg-blue-700 active:scale-95 ${base}`;
            case 'danger':
                return `text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/40 active:scale-95 border border-red-200/60 dark:border-red-800/40 ${base}`;
            case 'danger-solid':
                return `text-white bg-red-600 hover:bg-red-700 active:scale-95 ${base}`;
            case 'ghost':
                return `text-slate-500 dark:text-slate-400 hover:text-blue-700 dark:hover:text-[#00AAF1] active:scale-95 ${base}`;
            case 'default':
            default:
                return `text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-[#282828] hover:bg-slate-200 dark:hover:bg-[#333333] border border-slate-200/80 dark:border-[#383838] active:scale-95 ${base}`;
        }
    };

    const isAlert = item.type === 'ALERT';

    return (
        <div
            className={`
                timeline-card bg-white dark:bg-[#1f1f1f] rounded-2xl border p-5 shadow-2xs transition-all relative flex flex-col md:flex-row items-start justify-between gap-4
                ${item.read ? 'opacity-90 dark:opacity-85 border-slate-200/70 dark:border-[#2a2a2a]' : 'border-slate-200/90 dark:border-[#303030]'}
                ${isAlert ? 'hover:border-red-300 dark:hover:border-red-800/60' : 'hover:border-blue-300 dark:hover:border-blue-800/60'}
            `}
        >
            {/* Left Column: Selection, Indicator, Avatar, Context */}
            <div className="flex items-start gap-3.5 flex-1 min-w-0">
                {/* Checkbox and Unread Dot */}
                <div className="pt-1 flex items-center gap-2 flex-shrink-0">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect?.(item.id)}
                        className="rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-[#141414] text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <button
                        onClick={() => onToggleRead?.(item.id)}
                        title={item.read ? 'Mark as unread' : 'Mark as read'}
                        className="cursor-pointer"
                    >
                        <span
                            className={`unread-dot w-2 h-2 rounded-full inline-block ${
                                item.read ? 'bg-transparent' : 'bg-blue-600 dark:bg-[#00AAF1]'
                            }`}
                        />
                    </button>
                </div>

                {/* Actor Avatar */}
                <div
                    className={`w-10 h-10 rounded-full border flex items-center justify-center font-bold text-xs flex-shrink-0 shadow-2xs transition-all ${getActorAvatarStyles(
                        item.actor
                    )}`}
                >
                    {item.actor?.icon ? (
                        <span className="material-symbols-outlined text-base">{item.actor.icon}</span>
                    ) : (
                        item.actor?.initials || 'U'
                    )}
                </div>

                {/* Middle Content */}
                <div className="space-y-1.5 flex-1 min-w-0">
                    {/* Badge Row */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${getTypeTagStyles(
                                item.type
                            )}`}
                        >
                            {item.type}
                        </span>

                        {item.priority && (
                            <span
                                className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${getPriorityTagStyles(
                                    item.priorityLevel
                                )}`}
                            >
                                {item.priority}
                            </span>
                        )}

                        <span className="text-xs text-slate-500 dark:text-slate-400">• {item.timeAgo}</span>

                        {item.source && (
                            <span className="text-xs text-slate-400 dark:text-slate-500">via {item.source}</span>
                        )}
                    </div>

                    {/* Title */}
                    <h3
                        onClick={() => {
                            if (item.actions && item.actions.length > 0) {
                                onActionClick?.(item.actions[0].key, item);
                            }
                        }}
                        className={`text-base font-bold font-headline transition-colors cursor-pointer hover:text-[#00AAF1] ${
                            item.titleColor || 'text-[#003263] dark:text-[#a7c8ff]'
                        }`}
                        title="Click to view details"
                    >
                        {item.title}
                    </h3>

                    {/* Context Breadcrumbs */}
                    {item.context && (
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 font-medium">
                            {item.context.project && (
                                <>
                                    <span className="font-bold text-slate-700 dark:text-slate-200">Project:</span>
                                    <span>{item.context.project}</span>
                                </>
                            )}
                            {item.context.milestone && (
                                <>
                                    <span className="text-slate-300 dark:text-slate-600">|</span>
                                    <span className="font-bold text-slate-700 dark:text-slate-200">Milestone:</span>
                                    <span>{item.context.milestone}</span>
                                </>
                            )}
                            {item.context.deliverable && (
                                <>
                                    <span className="text-slate-300 dark:text-slate-600">|</span>
                                    <span className="font-bold text-slate-700 dark:text-slate-200">Deliverable:</span>
                                    <span>{item.context.deliverable}</span>
                                </>
                            )}
                            {item.context.task && (
                                <>
                                    <span className="text-slate-300 dark:text-slate-600">|</span>
                                    <span className="font-bold text-slate-700 dark:text-slate-200">Task:</span>
                                    <span>{item.context.task}</span>
                                </>
                            )}
                            {item.context.client && (
                                <>
                                    <span className="text-slate-300 dark:text-slate-600">|</span>
                                    <span className="font-bold text-slate-700 dark:text-slate-200">Client:</span>
                                    <span>{item.context.client}</span>
                                </>
                            )}
                            {item.context.sprint && (
                                <>
                                    <span className="text-slate-300 dark:text-slate-600">|</span>
                                    <span className="font-bold text-slate-700 dark:text-slate-200">Sprint:</span>
                                    <span>{item.context.sprint}</span>
                                </>
                            )}
                            {item.context.statusDetail && (
                                <>
                                    <span className="text-slate-300 dark:text-slate-600">|</span>
                                    <span className="font-bold text-emerald-600 dark:text-emerald-400">
                                        {item.context.statusDetail}
                                    </span>
                                </>
                            )}
                        </div>
                    )}

                    {/* Description narrative */}
                    {item.description && (
                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed pt-0.5">
                            {item.description}
                        </p>
                    )}

                    {/* Direct Mention Quote */}
                    {item.quote && (
                        <div className="bg-slate-50 dark:bg-[#141414] border-l-2 border-purple-500 pl-3 py-1.5 rounded-r-lg mt-1">
                            <p className="text-xs text-slate-700 dark:text-slate-300 italic">💬 "{item.quote}"</p>
                        </div>
                    )}

                    {/* Nested Tasks list */}
                    {item.nestedTasks && item.nestedTasks.length > 0 && (
                        <div className="bg-slate-50 dark:bg-[#141414] border border-slate-200/60 dark:border-[#303030] rounded-xl p-3 space-y-1.5 text-xs text-slate-700 dark:text-slate-300">
                            {item.nestedTasks.map((nt, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <span
                                        className={`material-symbols-outlined text-xs ${nt.iconColor}`}
                                    >
                                        {nt.icon}
                                    </span>
                                    <span>{nt.text}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Column: Actions */}
            <div className="flex items-center gap-2 flex-shrink-0 pl-10 md:pl-0">
                {item.actions?.map((act) => (
                    <button
                        key={act.key}
                        id={`btn-action-${item.id}-${act.key}`}
                        data-testid={`btn-action-${act.key}`}
                        onClick={() => onActionClick?.(act.key, item)}
                        className={getActionButtonClass(act.style)}
                        style={
                            act.style === 'brand-gradient'
                                ? { background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }
                                : undefined
                        }
                    >
                        {act.icon && (
                            <span className="material-symbols-outlined text-[13px] leading-none shrink-0">{act.icon}</span>
                        )}
                        <span className="leading-none text-xs font-bold">{act.label}</span>
                    </button>
                ))}

                {/* Bookmark Button */}
                <button
                    onClick={() => onToggleBookmark?.(item.id)}
                    title={item.bookmarked ? 'Remove bookmark' : 'Bookmark for later'}
                    className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
                        item.bookmarked
                            ? 'text-amber-500 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                            : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#252525]'
                    }`}
                >
                    <span className="material-symbols-outlined text-base leading-none">
                        {item.bookmarked ? 'bookmark' : 'bookmark_border'}
                    </span>
                </button>

                {/* Archive / Restore Button */}
                <button
                    onClick={() => onToggleArchive?.(item.id)}
                    title={item.archived ? 'Restore to active workspace' : 'Archive item'}
                    className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
                        item.archived
                            ? 'text-blue-600 dark:text-[#00AAF1] bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                            : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#252525]'
                    }`}
                >
                    <span className="material-symbols-outlined text-base leading-none">
                        {item.archived ? 'unarchive' : 'archive'}
                    </span>
                </button>
            </div>
        </div>
    );
}
